import React, { useEffect, useState } from 'react';
import { ClipboardList, Check, X, Loader } from 'lucide-react';
import { SCENARIOS_PRETS, SECTEURS_CHOIX, DIFFICULTES_CHOIX } from '../../utils/scenario';

/**
 * SCD Lot 5 — Panneau « Scénario de session » de la vue Supervision.
 * Le formateur choisit un scénario prêt à l'emploi ou en paramètre un
 * personnalisé (secteur imposé, difficulté, durée). Les apprenants le
 * reçoivent à la validation de leur licence (création d'entreprise).
 */
export default function ScenarioPanel({ backend }) {
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState({ nom: '', secteur: '', difficulte: '', duree_jours: 60 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${backend}/api/master/scenario`, { credentials: 'include' });
        if (res.ok && alive) setCurrent((await res.json()).scenario || null);
      } catch (_) { /* réseau — panneau reste utilisable */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [backend]);

  const save = async (scenario) => {
    setSaving(true);
    try {
      const res = await fetch(`${backend}/api/master/scenario`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario }),
      });
      if (res.ok) {
        setCurrent(scenario);
        setOpen(false);
      }
    } catch (_) { /* toast implicite via l'état inchangé */ }
    setSaving(false);
  };

  const saveCustom = () => {
    const sc = {
      id: 'custom',
      nom: custom.nom || 'Scénario personnalisé',
      secteur: custom.secteur || null,
      difficulte: custom.difficulte || null,
      duree_jours: Math.max(10, Math.min(360, parseInt(custom.duree_jours, 10) || 60)),
      objectifs: [],
    };
    save(sc);
  };

  return (
    <div className="bg-ink-800 border border-purple-500/30 rounded-lg p-4" data-testid="scenario-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-purple-300" />
          <p className="font-bold text-sm text-slate-100">Scénario de session</p>
          {loading && <Loader className="w-3 h-3 animate-spin text-slate-500" />}
        </div>
        {current ? (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-purple-200 bg-purple-500/15 border border-purple-500/40 rounded-full px-3 py-1" data-testid="scenario-actif">
              {current.nom} · {current.duree_jours} j
            </span>
            <button
              onClick={() => save(null)}
              disabled={saving}
              className="text-xs text-slate-400 hover:text-rose-300 transition-colors flex items-center gap-1"
              data-testid="scenario-clear-btn"
            >
              <X className="w-3 h-3" /> Désactiver
            </button>
          </div>
        ) : (
          <span className="text-xs text-slate-500 italic" data-testid="scenario-aucun">Aucun scénario — partie libre</span>
        )}
      </div>

      <p className="text-[11px] text-slate-500 mt-1">
        Le scénario s'applique aux apprenants à la création de leur entreprise (secteur/difficulté verrouillés, objectifs affichés, fin à J+durée avec score).
      </p>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
        {SCENARIOS_PRETS.map((sc) => (
          <button
            key={sc.id}
            onClick={() => save({ ...sc })}
            disabled={saving}
            className={`text-left rounded-lg border p-3 transition-all ${current?.id === sc.id ? 'border-purple-400 bg-purple-500/15' : 'border-ink-600 bg-ink-900/50 hover:border-purple-500/50'}`}
            data-testid={`scenario-pret-${sc.id}`}
          >
            <p className="text-sm font-bold text-slate-100 flex items-center gap-1.5">
              {sc.nom}
              {current?.id === sc.id && <Check className="w-3.5 h-3.5 text-purple-300" />}
            </p>
            <p className="text-[10px] text-slate-500 mt-1">{sc.description}</p>
            <p className="text-[10px] font-mono text-purple-300 mt-1">{sc.duree_jours} jours{sc.start_month ? ` · départ mois ${sc.start_month}` : ''}{sc.treasury_bonus ? ` · +${sc.treasury_bonus.toLocaleString('fr-FR')} €` : ''}</p>
          </button>
        ))}
      </div>

      <button
        onClick={() => setOpen(!open)}
        className="mt-2 text-xs text-purple-300 hover:text-purple-200 transition-colors"
        data-testid="scenario-custom-toggle"
      >
        {open ? '− Masquer' : '+ Scénario personnalisé (secteur, difficulté, durée)'}
      </button>

      {open && (
        <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2 items-end" data-testid="scenario-custom-form">
          <div className="col-span-2 md:col-span-1">
            <label className="text-[10px] uppercase text-slate-500">Nom</label>
            <input
              value={custom.nom}
              onChange={(e) => setCustom({ ...custom, nom: e.target.value })}
              placeholder="Session BTS MCO"
              className="w-full bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200"
              data-testid="scenario-custom-nom"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500">Secteur (imposé)</label>
            <select
              value={custom.secteur}
              onChange={(e) => setCustom({ ...custom, secteur: e.target.value })}
              className="w-full bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200"
              data-testid="scenario-custom-secteur"
            >
              <option value="">Libre</option>
              {SECTEURS_CHOIX.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500">Difficulté</label>
            <select
              value={custom.difficulte}
              onChange={(e) => setCustom({ ...custom, difficulte: e.target.value })}
              className="w-full bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200"
              data-testid="scenario-custom-difficulte"
            >
              <option value="">Libre</option>
              {DIFFICULTES_CHOIX.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500">Durée (jours)</label>
            <input
              type="number"
              min={10}
              max={360}
              value={custom.duree_jours}
              onChange={(e) => setCustom({ ...custom, duree_jours: e.target.value })}
              className="w-full bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200"
              data-testid="scenario-custom-duree"
            />
          </div>
          <button
            onClick={saveCustom}
            disabled={saving}
            className="rounded bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold px-3 py-2 transition-colors disabled:opacity-50"
            data-testid="scenario-custom-save"
          >
            {saving ? '…' : 'Activer'}
          </button>
        </div>
      )}
    </div>
  );
}
